import Link from 'next/link';
import ChevronRightIcon from '../icons/ChevronRightIcon';

export type BreadcrumbItem = {
  label: string;
  /** Omit on the last (current-page) item. */
  href?: string;
};

export type BreadcrumbsProps = {
  /** Trail after "Home" (which is always prepended). Last item = current page. */
  items: BreadcrumbItem[];
  /** `onDark` for breadcrumbs sitting on navy/photo hero backgrounds. */
  tone?: 'default' | 'onDark';
  /** Label/target of the leading root crumb. */
  rootLabel?: string;
  rootHref?: string;
  /** Replaces the default wrapper spacing (`mb-6`) when provided. */
  className?: string;
};

/**
 * Canonical storefront breadcrumb trail. One source of truth for typography,
 * separators, and a11y semantics (nav > ol > li with aria-current="page").
 */
export default function Breadcrumbs({
  items,
  tone = 'default',
  rootLabel = 'Home',
  rootHref = '/',
  className = 'mb-6',
}: BreadcrumbsProps) {
  const onDark = tone === 'onDark';
  const linkClass = onDark
    ? 'text-constant-white/70 transition-colors hover:text-constant-white'
    : 'text-text-secondary transition-colors hover:text-text-primary';
  const currentClass = onDark
    ? 'font-semibold text-constant-white'
    : 'font-medium text-text-primary';
  const chevronClass = onDark
    ? 'shrink-0 text-constant-white opacity-70'
    : 'shrink-0 opacity-60';

  const trail: BreadcrumbItem[] = [{ label: rootLabel, href: rootHref }, ...items];

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-1 text-body-16-medium font-medium">
        {trail.map((item, idx) => {
          const isLast = idx === trail.length - 1;
          return (
            <li key={`${item.label}-${idx}`} className="flex min-w-0 items-center gap-1">
              {idx > 0 && (
                <ChevronRightIcon
                  size={14}
                  color="currentColor"
                  className={chevronClass}
                  aria-hidden
                />
              )}
              {isLast || !item.href ? (
                <span className={`truncate ${currentClass}`} aria-current={isLast ? 'page' : undefined}>
                  {item.label}
                </span>
              ) : (
                <Link href={item.href} className={`truncate ${linkClass}`}>
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
